// Note Detail Page
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../services/api';
import { textToHtml, isHtml, sanitizeHtml } from '../utils/contentFormat';
import TelegramImage from '../components/TelegramImage';
import type { Note } from '../types/note';

export default function NoteDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [note, setNote] = useState<Note | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (id) {
      loadNote(id);
    }
  }, [id]);

  const loadNote = async (noteId: string) => {
    setIsLoading(true);
    const result = await apiClient.getNote(noteId);
    if (result.data) {
      setNote(result.data.note);
    }
    setIsLoading(false);
  };

  const handleDelete = async () => {
    if (!note) return;
    if (confirm('Are you sure you want to delete this note?')) {
      const result = await apiClient.deleteNote(note.id);
      if (result.data) {
        navigate('/notes');
      }
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const categoryColors: Record<string, string> = {
    idea: 'bg-purple-100 text-purple-800',
    expense: 'bg-green-100 text-green-800',
    important: 'bg-red-100 text-red-800',
    private: 'bg-gray-100 text-gray-800',
    reminder: 'bg-yellow-100 text-yellow-800',
    bookmark: 'bg-blue-100 text-blue-800',
  };

  if (isLoading) {
    return (
      <div className="text-center py-12 text-gray-500">
        <div className="animate-pulse">Loading note...</div>
      </div>
    );
  }

  if (!note) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 text-lg">Note not found</p>
        <button
          onClick={() => navigate('/notes')}
          className="mt-4 text-blue-600 hover:text-blue-700"
        >
          ← Back to Notes
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Breadcrumb */}
      <nav className="mb-6">
        <button
          onClick={() => navigate('/notes')}
          className="text-blue-600 hover:text-blue-700 flex items-center gap-2"
        >
          <span>←</span>
          <span>Back to Notes</span>
        </button>
      </nav>

      {/* Note Content */}
      <div className="bg-white rounded-lg shadow-md p-6 md:p-8">
        {/* Header */}
        <div className="flex justify-between items-start mb-6">
          <h1 className="text-3xl font-bold text-gray-900 flex-1">{note.title}</h1>
          <div className="flex gap-2 ml-4">
            <button
              onClick={() => navigate(`/notes/${note.id}/edit`)}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
            >
              Edit
            </button>
            <button
              onClick={handleDelete}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors"
            >
              Delete
            </button>
          </div>
        </div>

        {/* Meta Info */}
        <div className="flex flex-wrap gap-4 mb-6 pb-6 border-b">
          <span
            className={`px-3 py-1 rounded-full text-sm font-medium ${
              categoryColors[note.category] || 'bg-gray-100 text-gray-800'
            }`}
          >
            {note.category}
          </span>
          <span className="text-sm text-gray-500">
            Created: {formatDate(note.createdAt)}
          </span>
          {note.updatedAt !== note.createdAt && (
            <span className="text-sm text-gray-500">
              Updated: {formatDate(note.updatedAt)}
            </span>
          )}
        </div>

        {/* Content */}
        <div className="prose max-w-none mb-6">
          <div
            className="text-gray-700 leading-relaxed"
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(isHtml(note.content) ? note.content : textToHtml(note.content)),
            }}
          />
        </div>

        {/* Images */}
        {note.images && note.images.length > 0 && (
          <div className="mt-8">
            <h3 className="text-lg font-semibold mb-4 text-gray-900">Images</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {note.images.map((img, idx) => (
                <TelegramImage
                  key={idx}
                  imageUrl={img}
                  fileId={note.telegramFileIds?.[idx]}
                  alt={`Image ${idx + 1}`}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
